import type { PdfDocument } from './pdf-loader'

export type RenderedPage = {
  pageNumber: number
  canvas: HTMLCanvasElement
  width: number
  height: number
}

export class CanvasSizeError extends Error {
  width: number
  height: number
  constructor(width: number, height: number) {
    super(
      `Canvas size ${width}x${height} exceeds safe limits for this browser.`,
    )
    this.name = 'CanvasSizeError'
    this.width = width
    this.height = height
  }
}

const MAX_CANVAS_DIMENSION = 16384
const MAX_CANVAS_PIXELS = 268_435_456

/**
 * Validates that a canvas of the given dimensions can be safely
 * allocated in the current browser. Throws CanvasSizeError if not.
 */
export function validateCanvasSize(width: number, height: number): void {
  if (width > MAX_CANVAS_DIMENSION || height > MAX_CANVAS_DIMENSION) {
    throw new CanvasSizeError(width, height)
  }
  const pixelCount = width * height
  if (pixelCount > MAX_CANVAS_PIXELS) {
    throw new CanvasSizeError(width, height)
  }
}

/**
 * Renders a single PDF page to a canvas at the given scale.
 * Fills the canvas with a white background before rendering
 * so transparent areas don't turn black in JPEG output.
 *
 * Validates canvas dimensions before allocation (PRD §38).
 */
export async function renderPage(
  pdf: PdfDocument,
  pageNumber: number,
  scale: number,
): Promise<RenderedPage> {
  const page = await pdf.getPage(pageNumber)
  const viewport = page.getViewport({ scale })

  const width = Math.ceil(viewport.width)
  const height = Math.ceil(viewport.height)

  validateCanvasSize(width, height)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas 2D context not available')

  context.fillStyle = '#FFFFFF'
  context.fillRect(0, 0, canvas.width, canvas.height)

  await page.render({ canvasContext: context, viewport }).promise
  page.cleanup()

  return { pageNumber, canvas, width: canvas.width, height: canvas.height }
}
